import React from "react";
import { toast } from "react-toastify";
import API from "../utils/api";

const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return "0:00";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
};

const RecordingCell = ({ leadId, allocationId, recordingUrls, onAudioProgress }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [presignedUrls, setPresignedUrls] = React.useState({});
    const [loading, setLoading] = React.useState({});
    const [playingKey, setPlayingKey] = React.useState(null);
    const [paused, setPaused] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const audioRef = React.useRef(null);

    React.useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    let urls = [];
    if (recordingUrls) {
        try {
            urls = typeof recordingUrls === "string" ? JSON.parse(recordingUrls) : recordingUrls;
        } catch (_) {
            urls = [];
        }
    }

    if (!urls || urls.length === 0) return <span className="text-muted">-</span>;

    const fetchPresignedUrl = async (key) => {
        if (presignedUrls[key]) return presignedUrls[key];
        setLoading((prev) => ({ ...prev, [key]: true }));
        try {
            const res = await API.post("/admin/recording/presigned-url", {
                key,
                lead_id: leadId,
                allocation_id: allocationId,
            });
            const url = res.data.data.url;
            setPresignedUrls((prev) => ({ ...prev, [key]: url }));
            return url;
        } catch (e) {
            console.error("Failed to fetch presigned URL for recording", e);
            toast.error("Failed to fetch recording URL");
            return null;
        } finally {
            setLoading((prev) => ({ ...prev, [key]: false }));
        }
    };

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setPlayingKey(null);
        setPaused(false);
        setCurrentTime(0);
        setDuration(0);
    };

    const handlePlay = async (key) => {
        // Same track: toggle pause/resume
        if (playingKey === key) {
            if (paused) {
                audioRef.current.play();
                setPaused(false);
            } else {
                audioRef.current.pause();
                setPaused(true);
            }
            return;
        }

        // Different track: stop current first
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setPlayingKey(null);
        setPaused(false);
        setCurrentTime(0);
        setDuration(0);

        const url = await fetchPresignedUrl(key);
        if (!url) return;

        const audio = new Audio(url);
        audioRef.current = audio;

        audio.addEventListener("loadedmetadata", () => {
            setDuration(audio.duration);
        });
        audio.addEventListener("timeupdate", () => {
            setCurrentTime(audio.currentTime);
            if (onAudioProgress) {
                onAudioProgress(leadId, audio.currentTime, audio.duration);
            }
        });
        audio.addEventListener("ended", stopAudio);
        audio.addEventListener("error", () => {
            toast.error("Failed to play recording");
            stopAudio();
        });

        setPlayingKey(key);
        setPaused(false);
        audio.play();
    };

    const handleSeek = (e) => {
        if (!audioRef.current || !duration) return;
        const val = Number(e.target.value);
        audioRef.current.currentTime = val;
        setCurrentTime(val);
        if (onAudioProgress) {
            onAudioProgress(leadId, val, duration);
        }
    };

    const handleDownload = async (key, index) => {
        const url = await fetchPresignedUrl(key);
        if (!url) return;
        const fileName = key.split("/").pop() || `recording_${index + 1}.m4a`;
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div style={{ minWidth: 160 }}>
            <button
                className="btn btn-xs btn-info"
                onClick={() => setExpanded(!expanded)}
                title="Show recordings"
            >
                <i className={`fas fa-microphone${expanded ? "-slash" : ""}`}></i>{" "}
                {urls.length} recording{urls.length !== 1 ? "s" : ""}
            </button>
            {expanded && (
                <div style={{ marginTop: 6 }}>
                    {urls.map((key, index) => {
                        const filename = key.split("/").pop() || `Recording ${index + 1}`;
                        const isLoading = loading[key];
                        const isActive = playingKey === key;
                        const isPlaying = isActive && !paused;
                        return (
                            <div key={key} style={{ marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 6 }}>
                                {/* Row 1: filename + controls */}
                                <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "nowrap" }}>
                                    <span
                                        style={{ fontSize: 11, color: "#555", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110 }}
                                        title={filename}
                                    >
                                        {index + 1}. {filename}
                                    </span>
                                    {/* Play / Pause */}
                                    <button
                                        className={`btn btn-xs ${isPlaying ? "btn-warning" : "btn-success"}`}
                                        onClick={() => handlePlay(key)}
                                        disabled={isLoading}
                                        title={isPlaying ? "Pause" : "Play"}
                                    >
                                        {isLoading ? (
                                            <i className="fas fa-spinner fa-spin"></i>
                                        ) : (
                                            <i className={`fas fa-${isPlaying ? "pause" : "play"}`}></i>
                                        )}
                                    </button>
                                    {/* Stop — only when active */}
                                    {isActive && (
                                        <button
                                            className="btn btn-xs btn-danger"
                                            onClick={stopAudio}
                                            title="Stop"
                                        >
                                            <i className="fas fa-stop"></i>
                                        </button>
                                    )}
                                    {/* Download */}
                                    <button
                                        className="btn btn-xs btn-default"
                                        onClick={() => handleDownload(key, index)}
                                        disabled={isLoading}
                                        title="Download"
                                    >
                                        {isLoading ? (
                                            <i className="fas fa-spinner fa-spin"></i>
                                        ) : (
                                            <i className="fas fa-download"></i>
                                        )}
                                    </button>
                                </div>
                                {/* Row 2: progress bar — only when active */}
                                {isActive && (
                                    <div style={{ marginTop: 4 }}>
                                        <input
                                            type="range"
                                            min={0}
                                            max={duration || 0}
                                            step={0.1}
                                            value={currentTime}
                                            onChange={handleSeek}
                                            style={{ width: "100%", cursor: "pointer", accentColor: "#5bc0de" }}
                                        />
                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#888", marginTop: 1 }}>
                                            <span>{formatTime(currentTime)}</span>
                                            <span>{formatTime(duration)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RecordingCell;
